/**
 * useBackButtonClose
 * Reusable composable that intercepts the browser/mobile back button
 * to close a dialog/overlay instead of navigating away.
 *
 * Stack-aware: when overlays are nested (e.g. fullscreen image viewer over
 * the item dialog), one back press closes only the topmost open overlay.
 *
 * Usage:
 *   useBackButtonClose(isOpenRef, closeFunction)
 */
import { watch, onUnmounted } from 'vue'

// Module-level stack of open overlays (a single popstate listener serves all
// instances — per-instance listeners would all fire on one back press and
// close every open overlay at once)
const openStack = []
// Counts history.back() calls we issued ourselves (cleanup after a UI close),
// whose popstate must not be treated as a user back press
let pendingCleanupBacks = 0
let listenerInstalled = false

function ensureListener() {
  if (listenerInstalled) return
  listenerInstalled = true
  window.addEventListener('popstate', () => {
    if (pendingCleanupBacks > 0) {
      pendingCleanupBacks--
      return
    }
    // Back button pressed — close only the topmost open overlay
    const top = openStack[openStack.length - 1]
    if (top && top.isOpen.value) {
      openStack.pop()
      top.closingProgrammatically = true
      top.closeFn()
    }
  })
}

export function useBackButtonClose(isOpen, closeFn) {
  ensureListener()

  const entry = { isOpen, closeFn, closingProgrammatically: false }

  function removeFromStack() {
    const idx = openStack.indexOf(entry)
    if (idx !== -1) openStack.splice(idx, 1)
    return idx !== -1
  }

  watch(isOpen, (open) => {
    if (open) {
      // Overlay opened — push a history entry so back button triggers popstate
      window.history.pushState({ dialogOpen: true }, '')
      openStack.push(entry)
      entry.closingProgrammatically = false
    } else {
      const wasTracked = removeFromStack()
      if (wasTracked && !entry.closingProgrammatically) {
        // Closed by close button / ESC / overlay click — clean up history entry
        pendingCleanupBacks++
        window.history.back()
      }
      entry.closingProgrammatically = false
    }
  })

  onUnmounted(() => {
    // If overlay was open when component unmounts, clean up history
    if (removeFromStack()) {
      pendingCleanupBacks++
      window.history.back()
    }
  })

  /**
   * Neutralize the pushed history entry without calling history.back().
   * Call before programmatic navigation to prevent the async history.back()
   * from racing with router.push/replace and undoing the navigation.
   */
  function neutralizeHistory() {
    entry.closingProgrammatically = true
    removeFromStack()
  }

  return { neutralizeHistory }
}
